import {message} from '@ui/i18n/message';
import {Channel, CHANNEL_MODEL, ChannelContentItem} from '@common/channels/channel';
import {ChannelContentConfig} from '@common/admin/channels/channel-editor/channel-content-config';
import {GridViewIcon} from '@ui/icons/material/GridView';
import {ViewWeekIcon} from '@ui/icons/material/ViewWeek';
import {ViewListIcon} from '@ui/icons/material/ViewList';
import {Artist, ARTIST_MODEL} from '@app/web-player/artists/artist';
import {Album, ALBUM_MODEL} from '@app/web-player/albums/album';
import {Track, TRACK_MODEL} from '@app/web-player/tracks/track';
import {Playlist, PLAYLIST_MODEL} from '@app/web-player/playlists/playlist';
import {User, USER_MODEL} from '@ui/types/user';
import {Genre, GENRE_MODEL} from '@app/web-player/genres/genre';
import {AdPicture, AD_PICTURE_MODEL} from '@app/web-player/ads/ad';
import { PaginationResponse } from '@common/http/backend-response/pagination-response';
import { AlbumSubtype, AlbumType } from '@common/admin/use-album_taxonomies';
import { LIVE_EVENT_MODEL, LiveEvent } from '../live-events/interfaces/live-event';
import {Focus, FOCUS_MODEL} from '@app/web-player/focus/focus';
import {RADIO_MODEL, Radio} from '@app/web-player/radio/radio';

export const TYPES: AlbumType[] = [];
export const SUBTYPES: AlbumSubtype[] = [];

export enum Sort {
  popularDesc = 'popularity:desc',
  popularAsc = 'popularity:asc',
  userPreferenceDesc = 'user_preference:desc',
  recentDesc = 'created_at:desc',
  recentAsc = 'created_at:asc',
  releaseDateDesc = 'release_date:desc',
  releaseDateAsc = 'release_date:asc',
  curated = 'channelables.order:asc',
  attachDateDesc = 'channelables.created_at:desc',
  attachDateAsc = 'channelables.created_at:asc',
  startsAtDateDesc = "starts_at:desc",
  startsAtDateAsc = "starts_at:asc",
  endsAtDateDesc = "ends_at:desc",
  endsAtDateAsc = "ends_at:asc",
}
export enum Layout {
  grid = 'grid',
  table = 'trackTable',
  list = 'list',
  carousel = 'carousel',
  hero = 'hero',
}

enum Auto {
  topTracks = 'topTracks',
  newAlbums = 'newAlbums',
  playlistTracks = 'playlistTracks',
  playlistSeries = 'playlistSeries',
  topGenres = 'topGenres',
  nonEmptyGenres = 'nonEmptyGenres',
}

enum Restrictions {
  genre = 'genre',
  artist = 'artist',
}

const contentModels: ChannelContentConfig['models'] = {
  [ARTIST_MODEL]: {
    label: message('Artists'),
    sortMethods: [Sort.popularDesc, Sort.popularAsc, Sort.userPreferenceDesc, Sort.recentDesc, Sort.recentAsc],
    layoutMethods: [Layout.grid, Layout.carousel],
    autoUpdateMethods: [],
    restrictions: [Restrictions.genre],
  },
  // [ALBUM_MODEL]: {
  //   label: message('Albums'),
  //   sortMethods: [Sort.popularDesc, Sort.recentDesc, Sort.releaseDateDesc],
  //   layoutMethods: [Layout.grid, Layout.carousel],
  //   autoUpdateMethods: [Auto.newAlbums],
  //   restrictions: [Restrictions.genre],
  // },
  [ALBUM_MODEL]: {
    label: message('Medias'),
    sortMethods: [Sort.popularDesc, Sort.popularAsc, Sort.userPreferenceDesc, Sort.recentDesc, Sort.recentAsc, Sort.releaseDateDesc, Sort.releaseDateAsc],
    layoutMethods: [Layout.grid, Layout.carousel],
    autoUpdateMethods: [Auto.newAlbums],
    restrictions: [Restrictions.genre, Restrictions.artist],
    type_id: {
        options: TYPES,
        default: 'listAll',
    },
    subtype_id: {
        options: SUBTYPES,
        default: 'listAll',
    },
  },
  [TRACK_MODEL]: {
    label: message('Tracks'),
    sortMethods: [Sort.popularDesc, Sort.popularAsc, Sort.userPreferenceDesc, Sort.recentDesc, Sort.recentAsc, Sort.releaseDateDesc, Sort.releaseDateAsc],
    layoutMethods: [Layout.grid, Layout.table, Layout.list, Layout.carousel],
    autoUpdateMethods: [Auto.topTracks, Auto.playlistTracks],
    restrictions: [Restrictions.genre, Restrictions.artist],
  },
  [PLAYLIST_MODEL]: {
    label: message('Playlists'),
    sortMethods: [Sort.popularDesc, Sort.popularAsc, Sort.recentDesc, Sort.recentAsc],
    layoutMethods: [Layout.grid, Layout.carousel],
    autoUpdateMethods: [Auto.playlistSeries],
  },
  [USER_MODEL]: {
    label: message('Users'),
    sortMethods: [Sort.recentDesc, Sort.recentAsc],
    layoutMethods: [Layout.grid, Layout.carousel],
    autoUpdateMethods: [],
  },
  [GENRE_MODEL]: {
    label: message('Genres'),
    sortMethods: [Sort.popularDesc, Sort.popularAsc, Sort.recentDesc, Sort.recentAsc],
    layoutMethods: [Layout.grid, Layout.carousel],
    autoUpdateMethods: [Auto.topGenres, Auto.nonEmptyGenres],
  },
  [RADIO_MODEL]: {
    label: message('Radios'),
    sortMethods: [Sort.popularDesc, Sort.popularAsc, Sort.userPreferenceDesc, Sort.recentDesc, Sort.recentAsc],
    layoutMethods: [Layout.grid, Layout.carousel],
    autoUpdateMethods: [],
  },
  [CHANNEL_MODEL]: {
    label: message('Channels'),
    sortMethods: [],
    layoutMethods: [Layout.list],
  },
  [AD_PICTURE_MODEL]: {
    label: message('Ads'),
    sortMethods: [],
    layoutMethods: [Layout.list],
  },
  [LIVE_EVENT_MODEL]: {
    label: message('Live Events'),
    sortMethods: [Sort.startsAtDateDesc, Sort.startsAtDateAsc, Sort.endsAtDateDesc, Sort.endsAtDateAsc],
    layoutMethods: [Layout.grid, Layout.carousel],
  },
  [FOCUS_MODEL]: {
    label: message('Focus'),
    sortMethods: [Sort.curated],
    layoutMethods: [Layout.hero],
  },
};

const contentSortingMethods: Record<
  Sort,
  ChannelContentConfig['sortingMethods']['any']
> = {
  [Sort.popularDesc]: {
    label: message('Most popular first'),
  },
  [Sort.popularAsc]: {
    label: message('Most popular last'),
  },
  [Sort.userPreferenceDesc]: {
    label: message('Best match for user preferences'),
  },
  [Sort.recentDesc]: {
    label: message('Recently added first'),
  },
  [Sort.recentAsc]: {
    label: message('Recently added last'),
  },
  [Sort.curated]: {
    label: message('Curated (reorder below)'),
    contentTypes: ['manual'],
  },
  [Sort.attachDateDesc]: {
    label: message('Items recently added to channel first'),
  },
  [Sort.attachDateAsc]: {
    label: message('Items recently added to channel last'),
  },
  [Sort.releaseDateDesc]: {
    label: message('Most recent first (by release date)'),
  },
  [Sort.releaseDateAsc]: {
    label: message('Most recent last (by release date)'),
  },
  [Sort.startsAtDateDesc]: {
    label: message('Most recent first (by start date)'),
  },
  [Sort.startsAtDateAsc]: {
    label: message('Most recent last (by start date)'),
  },
  [Sort.endsAtDateDesc]: {
    label: message('Most recent first (by end date)'),
  },
  [Sort.endsAtDateAsc]: {
    label: message('Most recent last (by end date)'),
  },
};

const contentLayoutMethods: Record<
  Layout,
  ChannelContentConfig['layoutMethods']['any']
> = {
  [Layout.grid]: {
    label: message('Grid'),
    icon: <GridViewIcon />,
  },
  [Layout.table]: {
    label: message('Table'),
    icon: <ViewWeekIcon />,
  },
  [Layout.list]: {
    label: message('List'),
    icon: <ViewListIcon />,
  },
  [Layout.carousel]: {
    label: message('Carousel'),
  },
  [Layout.hero]: {
    label: message('Hero'),
  },
};

const contentAutoUpdateMethods: Record<
  Auto,
  ChannelContentConfig['autoUpdateMethods']['any']
> = {
  [Auto.topTracks]: {
    label: message('Top tracks'),
    providers: ['spotify', 'local'],
  },
  [Auto.newAlbums]: {
    label: message('New releases'),
    providers: ['spotify', 'local'],
  },
  [Auto.playlistTracks]: {
    label: message('Playlist tracks'),
    providers: ['spotify', 'local'],
    value: {
      label: message('Playlist ID'),
      inputType: 'text',
    },
  },
  [Auto.playlistSeries]: {
    label: message('Playlist series snapshots'),
    providers: ['local'],
    value: {
      label: message('Playlist series (slug or ID)'),
      inputType: 'text',
    },
  },
  [Auto.topGenres]: {
    label: message('Popular genres'),
    providers: ['lastfm', 'local'],
  },
  [Auto.nonEmptyGenres]: {
    label: message('Genres that have content'),
    providers: ['local'],
  },
};

const contentRestrictions: Record<
  Restrictions,
  ChannelContentConfig['restrictions']['any']
> = {
  [Restrictions.genre]: {
    label: message('Genre'),
    value: Restrictions.genre,
  },
  [Restrictions.artist]: {
    label: message('Artist'),
    value: Restrictions.artist,
  },
};

export const channelContentConfig: ChannelContentConfig = {
  models: contentModels,
  sortingMethods: contentSortingMethods,
  layoutMethods: contentLayoutMethods,
  autoUpdateMethods: contentAutoUpdateMethods,
  userSelectableLayouts: [Layout.grid, Layout.table, Layout.list],
  restrictions: contentRestrictions,
};

export type ChannelContentModel<T = ChannelContentItem> = (
  | AdPicture
  | Artist
  | Album
  | Track
  | Playlist
  | User
  | Genre
  | Radio
  | Channel
  | LiveEvent
  | Focus
) & {
  channelable_id?: number;
  channelable_order?: number;
  content?: PaginationResponse<T>;
};
